import { Button, Col, Row, Stack, Pagination } from "react-bootstrap"
import useUXStore from "../../api/ux/store"
import ButtonControl from "../../components/ButtonControl"
import CStack from "../../components/CStack"
import { useEffect, useState } from "react"
import Label from "../../components/Label"
import Input from "../../components/Input"
import Loading from "../../components/Loading"
import { AppData } from "../../api/apps/data"
import CompanyItem from "../../containers/Admin/CompanyItem"
import { useCompanies } from "../../api/companies/store"
import { useAuth } from "../../api/auth/store"
import { SUPPORT_ROLE } from "../../constants"

const defaultErr = { id: "", name: "" }
const ADD_COM = "addCom"

const AddComView = ({ onAdded = () => { } }) => {
    const [adding, setAdding] = useState(false)
    const [com, setCom] = useState({ id: "", name: "" })
    const [err, setErr] = useState(defaultErr)
    const { refreshDialog, hideDialog } = useUXStore(state => state)
    const { addCompany } = useCompanies(state => state)

    const onAdd = () => {
        setAdding(true)
        refreshDialog({
            id: ADD_COM,
            onClose: undefined,
        }, false)
        setErr(defaultErr)
        addCompany(com.id, com.name).then(code => {
            switch (code) {
                case "200":
                    hideDialog(ADD_COM)
                    onAdded()
                    break;
                case "app_enrolled": // already enrolled
                    setErr({ id: "Id already taken.", name: "" })
                    break;
                case "incomplete_param":
                    setErr({ id: "Required", name: "Required" })
                    break
                default:
                    console.error(code)
                    break;
            }
            if (code !== "200") {
                refreshDialog({
                    id: ADD_COM,
                }, true)
            }
        })
            .catch(err => {

            }).finally(() => {
                setAdding(false)
            })
    }
    return <>
        <Stack gap={2}>
            <Row>
                <Col><Label>Id</Label> </Col>
                <Col><Input disabled={adding} label={err.id} error value={com.id} onChange={(e: any) => setCom({ ...com, id: e.target.value.replace(/[^a-zA-Z0-9._]/g, '') })} /> </Col>
            </Row>
            <Row>
                <Col><Label>Name</Label> </Col>
                <Col><Input disabled={adding} label={err.name} error onChange={(e: any) => setCom({ ...com, name: e.target.value })} /> </Col>
            </Row>
            <Row>
                <Col><Button style={{ float: "right" }} onClick={onAdd} disabled={adding}>Add</Button></Col>
            </Row>
        </Stack>

    </>
}

const Companies = () => {
    const { refreshDialog, addToast } = useUXStore(state => state)
    const {
        loading,
        companies,
        totalCompanies,
        currentPage,
        setCurrentPage,
        retrieveCompanies,
        removeCompany,
        updateCompany,
        addCompany
    } = useCompanies(state => state)

    useEffect(() => {
        retrieveCompanies(currentPage)
    }, [currentPage])
    const onAdd = () => {
        refreshDialog({
            id: ADD_COM,
            title: "Add Company",
            visible: true,
            content: <AddComView onAdded={onAdded} />,
            onConfirm: undefined,
        }, true)
    }
    const onDelete = (id: string) => {
        removeCompany(id)
            .then(code => {
                switch (code) {
                    case "200":
                        addToast("", "Company removed successfully!")
                        break;
                    default:
                        addToast("Failed", "Failed to removed comapany!")
                        break;
                }
            })
            .catch(err => {
                console.error(err)
            })
    }
    const onAdded = () => {
        retrieveCompanies(currentPage)
    }
    const onUpdate = async (app: AppData) => {
        updateCompany(app)
            .then(res => {
                // TODO: add toast
            })
            .catch(err => {

            })
    }
    // Pagination calculations
    const pageSize = 30
    const totalPages = Math.ceil(totalCompanies / pageSize)
    const startRange = totalCompanies === 0 ? 0 : (currentPage - 1) * pageSize + 1
    const endRange = Math.min(currentPage * pageSize, totalCompanies)

    // Generate page numbers with ellipsis
    const generatePageNumbers = (): (number | string)[] => {
        if (totalPages <= 7) {
            return Array.from({ length: totalPages }, (_, i) => i + 1)
        }

        const pages: (number | string)[] = []

        // Always show first page
        pages.push(1)

        // Show ellipsis or page 2
        if (currentPage > 3) {
            pages.push('...')
        } else if (currentPage > 1) {
            pages.push(2)
        }

        // Show current and adjacent pages
        for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
            if (!pages.includes(i)) {
                pages.push(i)
            }
        }

        // Show ellipsis or page totalPages-1
        if (currentPage < totalPages - 2) {
            pages.push('...')
        } else if (currentPage < totalPages && totalPages > 2) {
            pages.push(totalPages - 1)
        }

        // Always show last page
        if (totalPages > 1) {
            pages.push(totalPages)
        }

        return pages
    }

    const handlePageChange = (page: number) => {
        if (page !== currentPage && page >= 1 && page <= totalPages) {
            setCurrentPage(page)
        }
    }

    const { hasRole } = useAuth(state => state)
    const canAdd = !hasRole(SUPPORT_ROLE)

    if (loading) return <Loading center />
    return <>
        {canAdd && <ButtonControl text="Add Company" buttonLabel="Add" onClicked={onAdd} />}
        <CStack header="Companies">
            {
                companies !== null && companies!.map((item, i) =>
                    <CompanyItem key={"com" + item.id!} com={item} onDelete={onDelete}
                        onUpdate={onUpdate} />)
            }
        </CStack>
        {!loading && totalCompanies > pageSize && (
            <div style={{ padding: "15px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ color: "#565656" }}>
                    Showing {startRange}-{endRange} of {totalCompanies} companies
                </div>
                <Pagination>
                    <Pagination.Prev
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                    />
                    {generatePageNumbers().map((page, index) => {
                        if (page === '...') {
                            return <Pagination.Ellipsis key={`ellipsis-${index}`} disabled />
                        }
                        return (
                            <Pagination.Item
                                key={page}
                                active={page === currentPage}
                                onClick={() => handlePageChange(page as number)}
                            >
                                {page}
                            </Pagination.Item>
                        )
                    })}
                    <Pagination.Next
                        disabled={currentPage === totalPages}
                        onClick={() => handlePageChange(currentPage + 1)}
                    />
                </Pagination>
            </div>
        )}
    </>
}

export default Companies